import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { DataService } from './data.service';
import { Item } from '../models/menu.model';
import { SelectedModifier } from '../models/cart.model';
export const ITEM_NOTE_MAX_LENGTH = 60;

export interface PendingPayment {
  orderIds: string[];
  createdAt: number;
}

export interface CartLine {
  id: string;
  item: Item;
  quantity: number;
  modifiers: SelectedModifier[];
  comment?: string;
  subSectionId?: string;
  isOptionalSubSection?: boolean;
}

interface PersistedCart {
  cartLines: CartLine[];
  lastActivityAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {

  private readonly CART_KEY = 'cart_data';
  private readonly dataService = inject(DataService);
  private readonly lines = signal<CartLine[]>([]);

  public readonly cartLines = this.lines.asReadonly();
  public readonly isEmpty = computed(() => this.lines().length === 0);

  private readonly INACTIVITY_EXPIRY_MS = 30 * 60 * 1000;
  private readonly EXPIRY_CHECK_INTERVAL_MS = 60 * 1000;

  // Marks an order that left for a gateway, so the cart can be reconciled later.
  private readonly PENDING_PAYMENT_KEY = 'pending_payment';
  // Older markers are an abandoned checkout, not a lost redirect.
  private readonly PENDING_PAYMENT_TTL_MS = 24 * 60 * 60 * 1000;

  private lastActivityAt: number = Date.now();

  constructor() {
    this.lines.set(this.loadCart());
    effect(() => {
      this.dataService.table();
      this.lines.set(this.loadCart());
    });
    this.startExpiryTimer();
  }

  private startExpiryTimer(): void {
    window.setInterval(() => {
      if (this.lines().length > 0 && Date.now() - this.lastActivityAt > this.INACTIVITY_EXPIRY_MS) {
        this.clearCart();
      }
    }, this.EXPIRY_CHECK_INTERVAL_MS);
  }

  public add(
    item: Item,
    modifiers: SelectedModifier[] = [],
    subSectionId?: string,
    isOptionalSubSection?: boolean,
    quantity = 1,
    comment?: string,
  ): void {
    comment = this.cleanNote(comment);
    const existing = this.findLine(item.id, modifiers, subSectionId, isOptionalSubSection, comment);

    this.lines.update(lines => existing
      ? lines.map(line => line.id === existing.id ? { ...line, quantity: line.quantity + quantity } : line)
      : [...lines, { id: uuidv4(), item, quantity, modifiers, subSectionId, isOptionalSubSection, ...(comment ? { comment } : {}) }]);

    this.saveCart();
  }

  public incrementLine(cartLineId: string): void {
    this.lines.update(lines => lines.map(line =>
      line.id === cartLineId ? { ...line, quantity: line.quantity + 1 } : line));
    this.saveCart();
  }

  public decrementLine(cartLineId: string): void {
    this.lines.update(lines => lines
      .map(line => line.id === cartLineId ? { ...line, quantity: line.quantity - 1 } : line)
      .filter(line => line.quantity > 0));
    this.saveCart();
  }

  public clearCart(): void {
    this.lines.set([]);
    this.saveCart();
    this.clearPendingPayment();
  }

  public removeLine(cartLineId: string): void {
    this.lines.update(lines => lines.filter(line => line.id !== cartLineId));
    this.saveCart();
  }

  public addItemComment(cartLineId: string, comment: string): void {
    this.lines.update(lines => lines.map(line => line.id === cartLineId ? { ...line, comment: this.cleanNote(comment) } : line));
    this.saveCart();
  }

  // Replaces one existing line's modifiers/quantity/comment in place. If the edit makes it
  // identical to another line (same item, modifiers and note), the two merge - quantities
  // summed - into the edited line's position, where the guest is looking, and the other goes.
  public updateLine(cartLineId: string, modifiers: SelectedModifier[], quantity: number, comment?: string): void {
    const edited = this.lines().find(line => line.id === cartLineId);
    if (!edited) return;

    comment = this.cleanNote(comment);
    const duplicate = this.findLine(edited.item.id, modifiers, edited.subSectionId, edited.isOptionalSubSection, comment, cartLineId);
    const mergedQuantity = quantity + (duplicate?.quantity ?? 0);

    this.lines.update(lines => lines
      .filter(line => line.id !== duplicate?.id)
      .map(line => line.id === cartLineId ? { ...line, modifiers, quantity: mergedQuantity, comment } : line));
    this.saveCart();
  }

  // The note is part of a line's identity: the same item with a different special instruction
  // is a different thing to the kitchen, so it gets its own line instead of merging into (and
  // overwriting the note of) an existing one.
  private findLine(
    itemId: string,
    modifiers: SelectedModifier[],
    subSectionId?: string,
    isOptionalSubSection?: boolean,
    comment?: string,
    excludeLineId?: string,
  ): CartLine | undefined {
    return this.lines().find(line =>
      line.id !== excludeLineId
      && line.item.id === itemId
      && (line.subSectionId || '') === (subSectionId || '')
      && (line.isOptionalSubSection ?? false) === (isOptionalSubSection ?? false)
      && this.sameNote(line.comment, comment)
      && this.sameModifiers(line.modifiers, modifiers));
  }

  // Every stored note goes through here: no leading/trailing spaces, capped at the limit,
  // and a blank note is no note.
  private cleanNote(note: string | undefined): string | undefined {
    return note?.trim().slice(0, ITEM_NOTE_MAX_LENGTH).trim() || undefined;
  }

  // "No onions", "no onions" and " no  onions " are the same instruction to the kitchen -
  // compare ignoring case and extra whitespace. On a merge the existing line keeps its text.
  private sameNote(a: string | undefined, b: string | undefined): boolean {
    const normalize = (note: string | undefined) => (note ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    return normalize(a) === normalize(b);
  }

  private sameModifiers(a: SelectedModifier[], b: SelectedModifier[]): boolean {
    if (a.length !== b.length) return false;

    const byId = (x: SelectedModifier, y: SelectedModifier): number => x.id.localeCompare(y.id);
    const sortedA = [...a].sort(byId);
    const sortedB = [...b].sort(byId);

    return sortedA.every((mod, i) => mod.id === sortedB[i].id && mod.quantity === sortedB[i].quantity);
  }

  private loadCart(): CartLine[] {
    const tableId = this.dataService.getTableId();
    if (!tableId) return [];

    const key = this.storageKey(tableId);
    const saved = localStorage.getItem(key);
    if (!saved) {
      this.lastActivityAt = Date.now();
      return [];
    }

    let parsed: Partial<PersistedCart> | undefined;
    try {
      parsed = JSON.parse(saved);
    } catch {
      parsed = undefined;
    }

    if (!parsed || !Array.isArray(parsed.cartLines)) {
      localStorage.removeItem(key);
      this.lastActivityAt = Date.now();
      return [];
    }

    const lastActivityAt = typeof parsed.lastActivityAt === 'number' ? parsed.lastActivityAt : Date.now();

    if (parsed.cartLines.length > 0 && Date.now() - lastActivityAt > this.INACTIVITY_EXPIRY_MS) {
      localStorage.removeItem(key);
      this.lastActivityAt = Date.now();
      return [];
    }

    this.lastActivityAt = lastActivityAt;
    return parsed.cartLines;
  }

  private saveCart(): void {
    const tableId = this.dataService.getTableId();
    if (!tableId) return;

    this.lastActivityAt = Date.now();
    const persisted: PersistedCart = { cartLines: this.lines(), lastActivityAt: this.lastActivityAt };
    localStorage.setItem(this.storageKey(tableId), JSON.stringify(persisted));
  }

  private storageKey(tableId: string): string {
    return `${this.CART_KEY}_${tableId}`;
  }

  private pendingPaymentKey(tableId: string): string {
    return `${this.PENDING_PAYMENT_KEY}_${tableId}`;
  }

  public setPendingPayment(orderIds: string[]): void {
    const tableId = this.dataService.getTableId();
    if (!tableId) return;

    const pending: PendingPayment = { orderIds, createdAt: Date.now() };
    localStorage.setItem(this.pendingPaymentKey(tableId), JSON.stringify(pending));
  }

  public getPendingPayment(): PendingPayment | null {
    const tableId = this.dataService.getTableId();
    if (!tableId) return null;

    const raw = localStorage.getItem(this.pendingPaymentKey(tableId));
    if (!raw) return null;

    try {
      const pending: PendingPayment = JSON.parse(raw);
      if (Date.now() - (pending.createdAt ?? 0) > this.PENDING_PAYMENT_TTL_MS) {
        this.clearPendingPayment();
        return null;
      }
      return pending;
    } catch {
      return null;
    }
  }

  public clearPendingPayment(): void {
    const tableId = this.dataService.getTableId();
    if (!tableId) return;
    localStorage.removeItem(this.pendingPaymentKey(tableId));
  }

  // Refreshes any cart line(s) for this item with fresh data (e.g. after a live menu
  // update), leaving modifiers untouched. Returns whether anything actually changed,
  // so callers only need to notify the user when it's true.
  public refreshItem(item: Item): boolean {
    let changed = false;

    // Built off a plain read, not .update(), and only written back when something actually
    // changed — .map() always returns a new array reference even when every line is passed
    // through unchanged, and writing that back unconditionally would fire a signal change
    // notification every time this runs, regardless of whether anything really changed
    // (a real problem for callers that react to cartLines() themselves — an infinite loop
    // if one of them is what calls refreshItem() in the first place).
    const updated = this.lines().map(line => {
      if (line.item.id !== item.id || !this.hasItemDataChanged(line.item, item)) return line;
      changed = true;
      return { ...line, item };
    });

    if (changed) {
      this.lines.set(updated);
      this.saveCart();
    }
    return changed;
  }

  // For when an item disappears from the menu entirely (e.g. disabled server-side)
  // rather than staying present with an updated status — there's no fresh Item to
  // refresh with, so it can no longer be ordered and is removed outright. Returns
  // whether a line was actually removed.
  public removeItem(itemId: string): boolean {
    const lines = this.lines();
    const remaining = lines.filter(line => line.item.id !== itemId);
    const removed = remaining.length !== lines.length;

    if (removed) {
      this.lines.set(remaining);
      this.saveCart();
    }
    return removed;
  }

  private hasItemDataChanged(current: Item, updated: Item): boolean {
    return current.price !== updated.price
      || current.status !== updated.status
      || current.name !== updated.name
      || current.description !== updated.description;
  }

}
